import { Box, Button, CircularProgress, Divider, Paper } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckIcon from '@mui/icons-material/Check';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { sharedColors, sharedStyles } from '../../../utils/Style';
import { TableColumn } from '../../../components/DataTable/TableColumn';
import { TextColumn } from '../../../components/DataTable/TextColumn';
import { DataTable } from '../../../components/DataTable/DataTable';
import { ShipmentImage } from '../../../interfaces/ShipmentImage/ShipmentImage';
import { ProductToCollect } from '../../../interfaces/ProductToCollect/ProductToCollect';

interface NameCellProps {
  product: ProductToCollect;
}

const NameCell = (props: NameCellProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.3,
      }}
    >
      <Typography sx={{ ...sharedStyles.body1 }}>
        {props.product.name}
      </Typography>

      {props.product.options.length > 0 && (
        <Typography sx={{ ...sharedStyles.body2, color: sharedColors.gray5 }}>
          {props.product.options
            .map((option) => option.optionValueName)
            .join(' / ')}
        </Typography>
      )}

      <Typography sx={{ ...sharedStyles.body2, color: sharedColors.gray5 }}>
        {`Barkod: ${props.product.barcode}`}
      </Typography>
    </Box>
  );
};

interface CollectOrderTableProps {
  products: ProductToCollect[];
  shipmentImages: ShipmentImage[];
  fullFilledProducts: ProductToCollect[];
  scannedAmounts: Record<string, number>;
  confirming: boolean;
  uploading: boolean;
  onClickScan: () => void;
  onUploadImage: (image: File) => void;
  onConfirm: () => void;
}

export const CollectOrderTable = (props: CollectOrderTableProps) => {
  const columns: TableColumn<ProductToCollect>[] = useMemo(
    () => [
      {
        id: 'name',
        name: 'Ürün',
        accessor: (row) => <NameCell product={row} />,
      },
      {
        id: 'amount',
        name: 'Miktar',
        accessor: (row) => (
          <TextColumn
            value={`${props.scannedAmounts[row.barcode ?? ''] ?? 0}/${
              row.quantity
            }`}
          />
        ),
      },
    ],
    [],
  );

  const isFullFilled =
    props.fullFilledProducts.length === props.products.length;

  return (
    <Paper
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
          Sipariş İçeriği
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
          <Button
            variant='contained'
            color='primary'
            onClick={props.onClickScan}
            startIcon={<QrCodeScannerIcon />}
            sx={{ ...sharedStyles.buttonText }}
          >
            Barkod Tara
          </Button>

          <Button
            variant='contained'
            startIcon={<PhotoCameraIcon />}
            color='primary'
            component='label'
            disabled={props.uploading || !isFullFilled}
            sx={{ ...sharedStyles.buttonText }}
          >
            Fotoğraf Çek
            <input
              type='file'
              accept='image/*'
              style={{ display: 'none' }}
              capture='environment'
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  props.onUploadImage(e.target.files[0]);
                }
              }}
            />
          </Button>

          <Button
            variant='contained'
            color='success'
            onClick={props.onConfirm}
            startIcon={<CheckIcon />}
            sx={{ ...sharedStyles.buttonText }}
            disabled={!isFullFilled || !props.shipmentImages.length}
          >
            Tamamla
          </Button>

          {props.confirming ||
            (props.uploading && <CircularProgress size={24} />)}
        </Box>
      </Box>

      <Divider />

      <DataTable
        columns={columns}
        totalCount={props.products.length}
        rows={props.products}
        selectedRows={props.fullFilledProducts}
        selectedRowColor={sharedColors.statusGreenLightest}
        hidePagination
      />
    </Paper>
  );
};
